import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useApi, type StoreDTO } from "../../api";
import { ExternalLink, TwoLine } from "../../components/cells";
import { crudEditor, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";

/**
 * Shopify storefronts.
 *
 * Rows can be added and edited but never deleted — a store id is referenced by
 * every order and artwork sync row, so the Vaadin grid offered no delete
 * either.
 */
export function StoresPage() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const allowed = useAllowed(LogicView.CONFIG_STORE);
  const title = t("navigation.item.stores", "Stores");

  const { data, isLoading, error } = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: allowed,
  });

  const clients = useQuery({
    queryKey: ["client"],
    queryFn: () => api.clients.findAll(),
    enabled: allowed,
  });

  const rows = data ?? [];
  const clientList = clients.data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["store"] });

  const cols = useMemo(() => {
    const c = defineColumns<StoreDTO>();
    const byId = new Map(clientList.map((entry) => [entry.id, entry]));
    return [
      c.text("code", (row) => row.code, { headerKey: "column.code", priority: 1 }),
      c.text("name", (row) => row.name, { headerKey: "column.name", priority: 1 }),
      c.custom(
        "currency",
        (row) => row.currency,
        (row) => (
          <Badge variant={row.currency === "USD" ? "success" : "info"}>{row.currency}</Badge>
        ),
        { headerKey: "column.currency", filter: "select", priority: 3 },
      ),
      c.custom(
        "is_default",
        (row) => String(row.is_default),
        (row) => (row.is_default ? <Badge variant="accent">✓</Badge> : <span>—</span>),
        { headerKey: "column.default", filter: "select", priority: 4 },
      ),
      c.custom(
        "url",
        (row) => row.url,
        (row) => (row.url ? <ExternalLink href={row.url}>{row.url}</ExternalLink> : <span>—</span>),
        { headerKey: "column.url", priority: 4 },
      ),
      c.text("gid", (row) => row.gid, { headerKey: "column.gid", defaultVisible: false }),
      c.custom(
        "client",
        (row) => (row.client_id ? (byId.get(row.client_id)?.name ?? "") : ""),
        (row) => {
          const client = row.client_id ? byId.get(row.client_id) : undefined;
          return client ? <TwoLine primary={client.name} secondary={client.code} /> : <span>—</span>;
        },
        { headerKey: "column.client", filter: "select", priority: 3 },
      ),
    ];
  }, [clientList]);

  const editor = useMemo(
    () =>
      crudEditor<StoreDTO>({
        client: api.stores,
        refresh,
        t,
        canDelete: false,
        fields: {
          code: textField(
            (row) => row.code,
            (row, value) => ({ ...row, code: value }),
            true,
          ),
          name: textField(
            (row) => row.name,
            (row, value) => ({ ...row, name: value }),
            true,
          ),
          currency: selectField(
            (row) => row.currency,
            (row, value) => ({ ...row, currency: value as StoreDTO["currency"] }),
            ["USD", "CAD"],
          ),
          url: textField(
            (row) => row.url,
            (row, value) => ({ ...row, url: value }),
            true,
          ),
          gid: textField(
            (row) => row.gid,
            (row, value) => ({ ...row, gid: value }),
            true,
          ),
          client: {
            kind: "select",
            get: (row) => (row.client_id ? String(row.client_id) : ""),
            set: (row, value) => ({ ...row, client_id: value ? Number(value) : null }),
            options: clientList.map((entry) => ({ value: String(entry.id), label: entry.name })),
          },
        },
        newRow: () => ({
          id: 0,
          code: "",
          name: "",
          currency: "CAD",
          is_default: false,
          url: "",
          gid: "",
          client_id: null,
        }),
        validate: (row) => {
          const errors: Record<string, string> = {};
          const required = t("form.validation.field.required", "Value is required");
          const unique = t("form.validation.field.unique", "Value already exists");
          (["code", "name", "url", "gid"] as const).forEach((key) => {
            if (!row[key]?.trim()) errors[key] = required;
            else if (rows.some((entry) => entry.id !== row.id && entry[key] === row[key])) {
              errors[key] = unique;
            }
          });
          return Object.keys(errors).length > 0 ? errors : null;
        },
      }),
    [api, clientList, rows, t],
  );

  if (!allowed) return <AccessDenied title={title} />;
  if (error) return <ErrorState title={title} error={error} />;

  return (
    <>
      <PageHeader title={title} />
      <DataTable<StoreDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        editor={editor}
      />
    </>
  );
}
